import { FeMapPlugin } from '@fe/maplibre-extensions';
import * as turf from '@turf/turf';
import { GeoJSONSource, LngLat, MapGeoJSONFeature, Map as MaplibreMap, MapMouseEvent, PointLike } from 'maplibre-gl';
import { EventBus } from '../../../app/contexts/event-bus';
import { greatCircle } from '../../../common/helpers';
import { UnitFormatter } from '../../../formatters';
import { FeServerRouteWaypointModel } from '../../../messages/message-json';
import assert from '../../../utils/assert';
import { AnalyzerRoute } from '../types';

export class RouteAnalyzerEdit implements FeMapPlugin {
    private editRouteSource: GeoJSONSource | null = null;
    private editPointsSource: GeoJSONSource | null = null;
    private originalRoute: AnalyzerRoute | null = null;
    private waypoints: FeServerRouteWaypointModel[] | null = null;
    private selectedVertexId: number | null = null;
    private mouseIsDown = false;
    private dragging = false;
    private map: MaplibreMap | null = null;

    constructor() {}

    public getOriginalRoute() {
        return this.originalRoute;
    }

    onAdd(map: MaplibreMap) {
        this.map = map;
        map.addSource('fe-route-analyzer-edit-line', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });

        map.addSource('fe-route-analyzer-edit-points', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });

        // Edit layers
        map.addLayer(
            {
                id: 'fe-route-analyzer-edit-line',
                source: 'fe-route-analyzer-edit-line',
                type: 'line',
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 2
                }
            },
            'fe-route-analyzer-slot'
        );

        map.addLayer(
            {
                id: 'fe-route-analyzer-edit-points',
                source: 'fe-route-analyzer-edit-points',
                type: 'circle',
                paint: {
                    'circle-radius': ['case', ['==', ['get', 'type'], 'waypoint'], 4, ['==', ['get', 'type'], 'midpoint'], 3, 3],
                    'circle-stroke-width': ['case', ['==', ['get', 'type'], 'waypoint'], 2, ['==', ['get', 'type'], 'midpoint'], 0, 0],
                    'circle-color': [
                        'case',
                        ['all', ['==', ['get', 'type'], 'waypoint'], ['==', ['boolean', ['get', 'active'], false], true]],
                        'rgba(0, 120, 250, 1)',
                        ['==', ['get', 'type'], 'waypoint'],
                        'rgba(0,0,0,0)',
                        ['==', ['get', 'type'], 'midpoint'],
                        'rgba(0, 120, 250, 1)',
                        'rgba(0,0,0,0)'
                    ],
                    'circle-stroke-color': 'rgba(0, 120, 250, 1)'
                }
            },
            'fe-route-analyzer-slot'
        );

        map.addLayer(
            {
                id: 'fe-route-analyzer-edit-labels',
                source: 'fe-route-analyzer-edit-points',
                type: 'symbol',
                layout: {
                    'text-field': ['get', 'label'],
                    'symbol-placement': 'point',
                    'text-anchor': 'top',
                    'text-allow-overlap': true,
                    'text-offset': [0, 0.7],
                    'text-size': 12,
                    'text-font': ['Noto Sans Regular']
                },
                paint: {
                    'text-color': ['global-state', 'text-color']
                }
            },
            'fe-route-analyzer-slot'
        );

        this.editRouteSource = <GeoJSONSource>map.getSource('fe-route-analyzer-edit-line');
        this.editPointsSource = <GeoJSONSource>map.getSource('fe-route-analyzer-edit-points');

        map.on('mousedown', this.mouseDown);
        map.on('mousemove', this.mouseMove);
        map.on('mouseup', this.mouseUp);
        document.addEventListener('keyup', this.onKeyUp);
    }

    onRemove(map: MaplibreMap) {
        map.off('mousedown', this.mouseDown);
        map.off('mousemove', this.mouseMove);
        map.off('mouseup', this.mouseUp);
        document.removeEventListener('keyup', this.onKeyUp);
        this.editRouteSource = null;
        this.editPointsSource = null;
        this.map = null;
    }

    edit(route: AnalyzerRoute) {
        this.selectedVertexId = null;
        this.originalRoute = route;
        // Copy waypoints to avoid modifying the original object
        this.waypoints = route.waypoints!.items.map((x) => ({ ...x }));
        this.setupEditRoute();
    }

    remove() {
        this.editRouteSource?.setData(turf.featureCollection([]));
        this.editPointsSource?.setData(turf.featureCollection([]));
        this.originalRoute = null;
        this.waypoints = null;
        this.dragging = false;
        this.selectedVertexId = null;
    }

    private mouseDown = (e: MapMouseEvent) => {
        if (!this.originalRoute) {
            return;
        }

        assert(this.waypoints);

        // Unselect vertex id if present
        if (this.selectedVertexId !== null) {
            this.editPointsSource?.updateData({
                update: [
                    {
                        id: this.originalRoute.id + this.selectedVertexId,
                        addOrUpdateProperties: [{ key: 'active', value: false }]
                    }
                ]
            });
        }

        const width = 10;
        const height = 10;
        const bounds: [PointLike, PointLike] = [
            [e.point.x - width / 2, e.point.y - height / 2],
            [e.point.x + width / 2, e.point.y + height / 2]
        ];

        const features = e.target.queryRenderedFeatures(bounds, { layers: ['fe-route-analyzer-edit-points'] });
        if (features.length === 0) {
            return;
        }

        const feature = features![0];
        let vertexId = feature.properties.vertexId as number;

        if (feature.properties.type === 'midpoint') {
            // Convert midpoint into a waypoint
            this.convertMidpointIntoWaypoint(feature.properties.vertexId, e.lngLat);
            // The selected vertex has moved so increment the id
            vertexId++;
        }
        // Regular waypoint
        else {
            // Don't let the first and last waypoint be moved
            if (vertexId === 0 || vertexId === this.waypoints.length - 1) {
                return;
            }
        }

        e.preventDefault();
        this.mouseIsDown = true;
        // Mark the point as selected
        this.editPointsSource?.updateData({
            update: [
                {
                    id: this.originalRoute.id + vertexId,
                    addOrUpdateProperties: [{ key: 'active', value: true }]
                }
            ]
        });
        this.selectedVertexId = vertexId;
    };

    private mouseMove = (e: MapMouseEvent) => {
        if (!this.mouseIsDown || this.selectedVertexId === null) {
            return;
        }

        this.dragging = true;
        assert(this.waypoints);
        assert(this.originalRoute);
        assert(this.map);

        const width = 10;
        const height = 10;
        const bounds: [PointLike, PointLike] = [
            [e.point.x - width / 2, e.point.y - height / 2],
            [e.point.x + width / 2, e.point.y + height / 2]
        ];

        let newWaypoint: [number, number];
        let newWaypointId: string;
        const features = this.map.queryRenderedFeatures(bounds, {
            layers: ['navaid', 'fix', 'fix_t']
        });

        const snapPoint = e.originalEvent.altKey ? null : getSnapPoint(e.lngLat, features);

        if (snapPoint) {
            newWaypoint = (snapPoint.geometry as GeoJSON.Point).coordinates as [number, number];
            newWaypointId = features[0].properties.id;
        } else {
            newWaypoint = e.lngLat.toArray();
            newWaypointId = UnitFormatter.formatLatLngNotamShort(newWaypoint);
        }

        const previousWaypoint = [this.waypoints[this.selectedVertexId - 1].ln, this.waypoints[this.selectedVertexId - 1].lt];
        const nextWaypoint = [this.waypoints[this.selectedVertexId + 1].ln, this.waypoints[this.selectedVertexId + 1].lt];

        this.editPointsSource?.updateData({
            update: [
                {
                    id: this.originalRoute.id + this.selectedVertexId,
                    newGeometry: {
                        type: 'Point',
                        coordinates: newWaypoint
                    },
                    addOrUpdateProperties: [
                        {
                            key: 'label',
                            value: newWaypointId
                        }
                    ]
                },
                {
                    id: this.originalRoute.id + (this.selectedVertexId - 1) + '.' + (this.selectedVertexId - 1),
                    newGeometry: turf.midpoint(previousWaypoint, newWaypoint).geometry
                },
                {
                    id: this.originalRoute.id + this.selectedVertexId + '.' + this.selectedVertexId,
                    newGeometry: turf.midpoint(nextWaypoint, newWaypoint).geometry
                }
            ]
        });

        this.editRouteSource?.updateData({
            update: [
                {
                    id: this.originalRoute.id + (this.selectedVertexId - 1),
                    newGeometry: greatCircle(previousWaypoint, newWaypoint)
                },
                {
                    id: this.originalRoute.id + this.selectedVertexId,
                    newGeometry: greatCircle(newWaypoint, nextWaypoint)
                }
            ]
        });

        const waypoint = this.waypoints![this.selectedVertexId];
        waypoint.id = newWaypointId;
        waypoint.ln = newWaypoint[0];
        waypoint.lt = newWaypoint[1];
    };

    private mouseUp = (e: MapMouseEvent) => {
        this.mouseIsDown = false;
        if (!this.dragging || this.selectedVertexId === null) {
            return;
        }

        this.dragging = false;
    };

    private onKeyUp = (e: KeyboardEvent) => {
        if (this.mouseIsDown || this.originalRoute === null) {
            return;
        }

        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            if (this.selectedVertexId !== null) {
                assert(this.waypoints);
                this.waypoints.splice(this.selectedVertexId, 1);
                this.selectedVertexId = null;
                this.setupEditRoute();
            }
        } else if (e.key === 'Escape') {
            if (this.originalRoute) {
                e.preventDefault();
                assert(this.map);
                EventBus.fire('route-analyzer-edit-cancel', { original: this.originalRoute });
            }
        } else if (e.key === 'Enter') {
            if (this.originalRoute) {
                e.preventDefault();
                assert(this.map);
                EventBus.fire('route-analyzer-edit-save', {
                    route: <AnalyzerRoute>{
                        ...this.originalRoute,
                        route: this.waypoints!.map((x) => x.id).join(' '),
                        waypoints: {
                            ...this.originalRoute.waypoints,
                            items: this.waypoints
                        }
                    }
                });
            }
        }
    };

    private setupEditRoute() {
        assert(this.originalRoute);
        assert(this.waypoints);

        const midPoints: GeoJSON.Feature[] = [];
        const segments: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>[] = [];

        for (let i = 0; i < this.waypoints.length - 1; i++) {
            const item = this.waypoints[i];
            const nextItem = this.waypoints[i + 1];
            const a = [item.ln, item.lt];
            const b = [nextItem.ln, nextItem.lt];

            const midPoint = turf.midpoint(a, b);
            midPoint.id = this.originalRoute.id + i + '.' + i;
            midPoint.properties!.vertexId = i;
            midPoint.properties!.type = 'midpoint';
            midPoints.push(midPoint);

            segments.push({
                type: 'Feature',
                id: this.originalRoute.id + i,
                geometry: greatCircle(a, b),
                properties: { vertexId: i, color: this.originalRoute.color }
            });
        }

        this.editRouteSource?.setData({
            type: 'FeatureCollection',
            features: segments
        });

        this.editPointsSource?.setData({
            type: 'FeatureCollection',
            features: [
                ...this.waypoints.map((x, i) => ({
                    type: 'Feature',
                    id: this.originalRoute!.id + i,
                    geometry: {
                        type: 'Point',
                        coordinates: [x.ln, x.lt]
                    },
                    properties: {
                        type: 'waypoint',
                        vertexId: i,
                        label: x.id
                    }
                })),
                ...midPoints
            ] as GeoJSON.Feature[]
        });
    }

    private convertMidpointIntoWaypoint(vertexId: number, point: LngLat) {
        assert(this.waypoints);
        assert(this.originalRoute);

        // Modify the route
        this.waypoints = [
            ...this.waypoints.slice(0, vertexId + 1),
            { id: UnitFormatter.formatLatLngNotamShort(point.toArray()), ln: point.lng, lt: point.lat },
            ...this.waypoints.slice(vertexId + 1)
        ];

        // And setup edit route again, this may seem inefficient but we need to update all the points to the right of the modified midpoint
        this.setupEditRoute();
    }
}

function getSnapPoint(mouse: LngLat, features: MapGeoJSONFeature[]): MapGeoJSONFeature | null {
    let dist = 99999999;
    let found: MapGeoJSONFeature | null = null;
    for (const feature of features) {
        if (feature.geometry.type !== 'Point') {
            continue;
        }

        const newDist =
            Math.pow(mouse.lng - (<GeoJSON.Point>feature.geometry).coordinates[0], 2) +
            Math.pow(mouse.lat - (<GeoJSON.Point>feature.geometry).coordinates[1], 2);
        if (newDist < dist) {
            dist = newDist;
            found = feature;
        }
    }
    return found;
}
